import { ArchiveJobStatusEnum } from '@gr/shared/enums/domain/archive/archive-job-status.enum';
import { ArchiveStageEnum } from '@gr/shared/enums/domain/archive/archive-stage.enum';

// A union, not a runtime allowlist: these names select a static pgTyped query from a lookup map, so an
// unrecognised one resolves no query and never reaches SQL.
export type ArchiveSourceTableName = 'messages' | 'conversations' | 'links' | 'link_clicks';

export const ARCHIVE_SOURCE_TABLE_NAMES: readonly ArchiveSourceTableName[] = [
  'messages',
  'conversations',
  'links',
  'link_clicks',
];

export function isArchiveSourceTableName(tableName: string): tableName is ArchiveSourceTableName {
  return ARCHIVE_SOURCE_TABLE_NAMES.some((name) => name === tableName);
}

export type ArchiveExportTableName =
  | 'archived_messages'
  | 'archived_conversations'
  | 'archived_links'
  | 'archived_link_clicks';

export const ARCHIVE_EXPORT_TABLE_NAMES: readonly ArchiveExportTableName[] = [
  'archived_messages',
  'archived_conversations',
  'archived_links',
  'archived_link_clicks',
];

export function isArchiveExportTableName(tableName: string): tableName is ArchiveExportTableName {
  return ARCHIVE_EXPORT_TABLE_NAMES.some((name) => name === tableName);
}

// tableName is deliberately string below: it comes from a text column whose CHECK constraint TypeScript
// cannot see, so only the guards above prove it — typing it would delete the reason they exist.

// No retentionInterval field: the configuration query resolves it to an absolute timestamp once.
export interface IArchiveConfiguration {
  id: string;
  tableName: string;
  stage: ArchiveStageEnum;
  batchSize: number;
  publishOrder: number;
  retentionIntervalText: string; // raw interval, for logging only
  retentionBoundaryAt: Date;
}

// The worker derives its entire position from this row, so a queue message carries only an id.
export interface IArchiveJob {
  id: string;
  archiveConfigurationId: string;
  tableName: string;
  stage: ArchiveStageEnum;
  batchSize: number;
  status: ArchiveJobStatusEnum;
  round: number;
  rowsMoved: number;
  batchAttempts: number;
  anchorDate: Date | null;
  retentionBoundaryAt: Date; // which rows qualify
  stopNewWorkAt: Date; // when work stops starting — conflating it with the cutoff above crosses 08:00 ET
}

// Terminal implies "already has its datapoint", since a terminal close is the only thing that emits.
// The reap needs this: STALLED is terminal yet still holds uidx_archive_jobs_in_flight.
const TERMINAL_ARCHIVE_JOB_STATUSES: readonly ArchiveJobStatusEnum[] = [
  ArchiveJobStatusEnum.COMPLETED,
  ArchiveJobStatusEnum.FAILED,
  ArchiveJobStatusEnum.STALLED,
  ArchiveJobStatusEnum.SKIPPED,
];

export function isTerminalArchiveJobStatus(status: ArchiveJobStatusEnum): boolean {
  return TERMINAL_ARCHIVE_JOB_STATUSES.includes(status);
}

// A job left in a live status past its nightly window.
export interface IStaleArchiveJob {
  id: string;
  tableName: string;
  stage: ArchiveStageEnum;
  status: ArchiveJobStatusEnum;
  round: number;
  rowsMoved: number;
  batchAttempts: number; // carried so the reap can emit the job's only metric datapoint
  startedAt: Date | null;
}

// The orchestrator's invocation payload. A scheduled run sends {} — the cron targets pass input: {} —
// so force is absent unless somebody typed it into a manual invoke.
export interface IArchiveOrchestrationEvent {
  force?: boolean;
}

// The SQS payload. Round, counters, anchor and cutoffs all live on the archive_jobs row instead, so a
// redelivered message can never resume from a stale position.
export interface IArchiveWorkMessage {
  archiveJobId: string;
  messageGroupId: string;
}

// For a result whose job row could not be read. Each sentinel's casing matches the values it stands in
// for.
export const UNKNOWN_ARCHIVE_STAGE = 'UNKNOWN';
export const UNKNOWN_ARCHIVE_TABLE_NAME = 'unknown';

export type ArchiveWorkerResultStage = ArchiveStageEnum | typeof UNKNOWN_ARCHIVE_STAGE;

// Carries everything the metric emitter needs, so a caller closing a job outside processArchiveJob can
// still emit rather than closing the row silently.
export interface IArchiveWorkerResult {
  reEnqueue: boolean;
  status: ArchiveJobStatusEnum;
  rowsMoved: number;
  tableName: string;
  stage: ArchiveWorkerResultStage;
  batchAttempts: number;
  // Separates the two runs COMPLETED describes: drained the table, or stopped at the cutoff with rows
  // still eligible. False on every failure and on a re-enqueue.
  isCaughtUp: boolean;
  // Set when the close found the row already terminal, so the writer that beat us owns the datapoint.
  // Emitting here too would double-count, and the status this result carries is not the row's.
  wasClosedByAnotherWriter?: boolean;
}
